import axios from 'axios';
import router from './router';
import store from './store';
import { message } from 'ant-design-vue';
import { getSessionContext } from './utils/session-context';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let activeRequests = 0;
let errorMessageShown = false;

const showLoader = () => {
  if (activeRequests === 0) {
    store.dispatch('showLoader');
  }
  activeRequests++;
};

const hideLoader = () => {
  if(activeRequests <= 0){
    activeRequests = 1
  }
  activeRequests--;
  if (activeRequests === 0) {
    store.dispatch('hideLoader');
  }
};

apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('authToken');
  const { subdomain, companyId } = getSessionContext();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    if (subdomain) {
      config.headers['X-Subdomain'] = subdomain;
    }
    if (companyId) {
      config.headers['X-Company-Id'] = companyId;
    }
    // Skip global loader if skipGlobalLoader flag is set
    if (!config.skipGlobalLoader) {
      showLoader();
    }
  } else {
    if(config?.url === '/forgot-password'){
      router.push('/forgot-password');
    } else{
      router.push('/'); // Redirect to login page if token is missing
    }
  }
  
  return config;
}, error => {
  hideLoader();
  return Promise.reject(error);
});

apiClient.interceptors.response.use(
  response => {
    // Only hide loader if it wasn't skipped in the request
    if (!response.config.skipGlobalLoader) {
      hideLoader();
    }
    return response;
  },
  async error => {
    // Only hide loader if it wasn't skipped in the request
    if (!error.config?.skipGlobalLoader) {
      hideLoader();
    }
    const statusCode = error?.response?.status;
    const errorMessage = String(error?.response?.data?.message || '').toLowerCase();
    const hasToken = !!localStorage.getItem('authToken');

    if (statusCode === 400 && errorMessage.includes('x-subdomain') && hasToken) {
      message.error('Tenant context missing. Please sign in again.');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      router.push('/');
      return Promise.reject(error);
    }

    if (statusCode === 403 && errorMessage.includes('invalid subdomain') && hasToken) {
      message.error('Tenant mismatch detected. Please log in with the correct tenant.');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      router.push('/');
      return Promise.reject(error);
    }

    if (statusCode === 404 && errorMessage.includes('tenant not found') && hasToken) {
      message.error('Tenant not found. Please contact support.');
      return Promise.reject(error);
    }

    if (error.response && (statusCode === 401 || statusCode === 403) && hasToken) {
      if (!errorMessageShown) {
        errorMessageShown = true;
        message.error("Session expired. Please log in again");
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('company');
        router.push('/'); // Redirect to the login page
      }
    }
    return Promise.reject(error);
  }
);

// Reset error message flag when a new API call starts
apiClient.interceptors.request.use(config => {
  errorMessageShown = false;
  return config;
});

export default apiClient;
